// Nutrition breakdown (Swift NutritionBreakdownSheet): donut, calories, macros, micros,
// coverage and the two micronutrient toggles. Shown per serving.
import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Sheet from './Sheet';
import Donut from './Donut';
import { Caption, EmptyState, Section, ToggleRow } from './ui';
import { dailyTargets, macroSlices, MICRONUTRIENTS, MICRONUTRIENT_WARNING_RATIO, summarize } from '../logic/nutrition';
import { useAllIngredients, useLibrary } from '../store/library';
import { useSettings } from '../store/settings';
import { font, radius, spacing, usePalette } from '../theme';
import type { IngredientLine } from '../types';

function Value({ label, value, unit, of }: { label: string; value: number; unit: string; of?: string }) {
  const p = usePalette();
  return (
    <View style={{ flexDirection: 'row', alignItems: 'baseline', paddingVertical: spacing.s, borderBottomWidth: 0.5, borderBottomColor: p.border, gap: spacing.s }}>
      <Text style={{ flex: 1, fontSize: font.body, color: p.text }}>{label}</Text>
      <Text style={{ fontSize: font.body, color: value === 0 ? p.secondaryText : p.text, fontVariant: ['tabular-nums'] }}>{value.toFixed(1)} {unit}</Text>
      {of ? <Caption>{of}</Caption> : null}
    </View>
  );
}

export default function NutritionSheet({ visible, onClose, lines, servings, title, caption }: { visible: boolean; onClose: () => void; lines: IngredientLine[]; servings: number; title?: string; caption?: string }) {
  const { t } = useTranslation();
  const p = usePalette();
  const ingredients = useAllIngredients();
  const units = useLibrary(s => s.units);
  const settings = useSettings();
  const summary = useMemo(() => summarize(lines, ingredients, units, servings), [lines, ingredients, units, servings]);
  const targets = dailyTargets(settings.dietProfile);
  const slices = macroSlices(summary).map(s => ({ ...s, title: t(`nutrition.${s.title === 'Carbs' ? 'carbs' : s.title.toLowerCase()}` as 'nutrition.sugar') }));

  const micros = MICRONUTRIENTS.filter(m => (summary.micros[m.key] ?? 0) > 0 && (settings.showsAdvancedMicronutrients || !m.advanced));
  const groupTarget = (category: string) => (category === 'Vitamins' ? targets.vitamins : category === 'Minerals' ? targets.minerals : undefined);
  const order = (c: string) => ({ Vitamins: 0, Minerals: 1, 'Common nutrients': 2, 'Advanced nutrients': 3 } as Record<string, number>)[c] ?? 4;
  const level = (value: number, target?: number) => (!target ? 2 : value / target >= 1 ? 0 : value / target >= MICRONUTRIENT_WARNING_RATIO ? 1 : 2);
  const planOf = (key: string) => {
    const target = targets.micros[key];
    return target ? t(target.max ? 'nutrition.ofDailyMax' : 'nutrition.ofDaily', { value: target.text }) : undefined;
  };
  const sortedMicros = [...micros].sort((a, b) => order(a.category) - order(b.category) || level(summary.micros[a.key], groupTarget(a.category)) - level(summary.micros[b.key], groupTarget(b.category)));

  return (
    <Sheet visible={visible} onClose={onClose} title={title ?? t('nutrition.title')} full>
      <Caption>{caption ?? t('nutrition.perServing')}</Caption>
      {slices.length ? <Donut slices={slices} /> : <EmptyState icon="nutrition" title={t('nutrition.noMacros')} />}
      <Caption>{t(settings.dietProfile ? 'nutrition.dailyPlan' : 'nutrition.dailyOptimum', { value: Math.round(targets.calories) })}</Caption>
      <View style={{ backgroundColor: p.card, borderRadius: radius.l, paddingHorizontal: spacing.l }}>
        <Value label={t('nutrition.calories')} value={summary.calories} unit="kcal" of={t('nutrition.ofDaily', { value: Math.round(targets.calories) })} />
      </View>
      <Section title={t('nutrition.macronutrients')}>
        <View style={{ backgroundColor: p.card, borderRadius: radius.l, paddingHorizontal: spacing.l }}>
          {(['protein', 'fat', 'carbohydrates', 'sugar', 'fiber'] as const).map(k => (
            <Value key={k} label={t(`nutrition.${k}`)} value={summary[k]} unit="g" of={t('nutrition.ofDaily', { value: Math.round(targets[k]) })} />
          ))}
        </View>
      </Section>
      {settings.showsMicronutrients && sortedMicros.length ? (
        <Section title={t('nutrition.micronutrients')}>
          <View style={{ backgroundColor: p.card, borderRadius: radius.l, paddingHorizontal: spacing.l }}>
            {sortedMicros.map(m => (
              <Value key={m.key} label={m.title} value={summary.micros[m.key]} unit="mg" of={planOf(m.key)} />
            ))}
          </View>
        </Section>
      ) : null}
      <Section title={t('nutrition.coverage')}>
        <Caption>{t('nutrition.known')}: {summary.knownIngredients}</Caption>
        <Caption>{t('nutrition.missing')}: {summary.missingTitles.length}{summary.missingTitles.length ? ` (${[...new Set(summary.missingTitles)].slice(0, 8).join(', ')})` : ''}</Caption>
      </Section>
      <ToggleRow label={t('nutrition.showMicros')} value={settings.showsMicronutrients} onChange={v => settings.set({ showsMicronutrients: v, ...(v ? {} : { showsAdvancedMicronutrients: false }) })} />
      <ToggleRow label={t('nutrition.showAdvanced')} note={t('nutrition.advancedNote')} value={settings.showsAdvancedMicronutrients} onChange={v => settings.set({ showsAdvancedMicronutrients: v, ...(v ? { showsMicronutrients: true } : {}) })} />
    </Sheet>
  );
}
